import React, { useEffect, useState } from 'react';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { DollarSignIcon, BarChartIcon, TrendingUpIcon, ArrowRightIcon, CheckIcon, XIcon, UserIcon } from 'lucide-react';
export const PricingStrategiesPage = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="w-full bg-gray-50 dark:bg-[#0A1C1A] min-h-screen" data-id="element-5161">
      <Navbar scrolled={scrolled} data-id="element-5162" />
      <main className="pt-24 pb-20" data-id="element-5163">
        <div className="container mx-auto px-4 md:px-6" data-id="element-5164">
          <div className="max-w-4xl mx-auto" data-id="element-5165">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-xl p-8 md:p-12 mb-12 text-white relative overflow-hidden" data-id="element-5166">
              <div className="absolute right-0 bottom-0 opacity-10" data-id="element-5167">
                <DollarSignIcon className="h-64 w-64" data-id="element-5168" />
              </div>
              <div className="relative z-10" data-id="element-5169">
                <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-5170">
                  Smart Pricing Strategies for Digital Creators
                </h1>
                <p className="text-xl opacity-90 max-w-3xl" data-id="element-5171">
                  Learn how to set the right price for your templates, UI kits,
                  and digital assets using psychological pricing, A/B testing,
                  and competitive research.
                </p>
              </div>
            </div>
            {/* Main content */}
            <div className="bg-white dark:bg-[#162825] rounded-xl p-8 md:p-10 shadow-sm mb-10" data-id="element-5172">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-5173">
                Why Pricing Matters
              </h2>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6" data-id="element-5174">
                Setting the right price for your digital products is one of the
                most critical decisions you'll make as a creator. Price too
                high, and you might scare away potential customers. Price too
                low, and you devalue your work while leaving money on the table.
              </p>
              <p className="text-gray-600 dark:text-[#B0C1BD] mb-6" data-id="element-5175">
                The good news is that digital products have unique economics
                that work in your favor - once created, they can be sold
                infinitely with minimal additional costs. This guide will help
                you navigate pricing strategies that maximize both sales volume
                and revenue.
              </p>
              <div className="border-l-4 border-[#2DB489] dark:border-[#4ED1A0] pl-6 my-8 py-2" data-id="element-5176">
                <p className="text-lg font-medium text-gray-800 dark:text-[#F8FAF9] italic" data-id="element-5177">
                  "The right price is not about charging as much as possible -
                  it's about finding the sweet spot where customers feel they're
                  getting excellent value while you're being fairly compensated
                  for your expertise."
                </p>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mt-12 mb-6" data-id="element-5178">
                Psychological Pricing Techniques
              </h2>
              <div className="mb-8" data-id="element-5179">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4 flex items-center" data-id="element-5180">
                  <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-8 h-8 rounded-full flex items-center justify-center mr-3" data-id="element-5181">
                    1
                  </span>
                  Charm Pricing
                </h3>
                <p className="text-gray-600 dark:text-[#B0C1BD] mb-4 ml-11" data-id="element-5182">
                  Ending prices with .99 or .97 (e.g., $19.99 instead of $20)
                  has been proven to increase sales. Studies show that consumers
                  tend to focus on the first digit and perceive these prices as
                  significantly lower.
                </p>
                <div className="ml-11 grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-5183">
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-lg" data-id="element-5184">
                    <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-5185">
                      Example:
                    </p>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5186">
                      A UI kit priced at $49.99 often psychologically feels
                      closer to $40 than $50 to customers.
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-lg" data-id="element-5187">
                    <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-5188">
                      When to use:
                    </p>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5189">
                      For products targeting price-sensitive customers or in
                      competitive niches.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mb-8" data-id="element-5190">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4 flex items-center" data-id="element-5191">
                  <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-8 h-8 rounded-full flex items-center justify-center mr-3" data-id="element-5192">
                    2
                  </span>
                  Prestige Pricing
                </h3>
                <p className="text-gray-600 dark:text-[#B0C1BD] mb-4 ml-11" data-id="element-5193">
                  Using round numbers (e.g., $100 instead of $99.99) can signal
                  quality and premium positioning. This works well for high-end
                  digital products where you want to emphasize craftsmanship and
                  value.
                </p>
                <div className="ml-11 grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-5194">
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-lg" data-id="element-5195">
                    <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-5196">
                      Example:
                    </p>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5197">
                      A comprehensive design system priced at $200 rather than
                      $199 can be perceived as more professional and
                      high-quality.
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-lg" data-id="element-5198">
                    <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-5199">
                      When to use:
                    </p>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5200">
                      For premium products where you want to signal exceptional
                      quality and comprehensive value.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mb-8" data-id="element-5201">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4 flex items-center" data-id="element-5202">
                  <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-8 h-8 rounded-full flex items-center justify-center mr-3" data-id="element-5203">
                    3
                  </span>
                  Anchoring
                </h3>
                <p className="text-gray-600 dark:text-[#B0C1BD] mb-4 ml-11" data-id="element-5204">
                  Showing a higher "original" price next to your actual price
                  creates a reference point that makes your current price seem
                  like a better deal.
                </p>
                <div className="ml-11 grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-5205">
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-lg" data-id="element-5206">
                    <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-5207">
                      Example:
                    </p>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5208">
                      Displaying "$149 $99" for your website template makes $99
                      seem like an excellent value.
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-[#1A3330] p-4 rounded-lg" data-id="element-5209">
                    <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-5210">
                      When to use:
                    </p>
                    <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5211">
                      During promotions, launches, or when you want to emphasize
                      the value proposition.
                    </p>
                  </div>
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mt-12 mb-6" data-id="element-5212">
                Practical Pricing Framework
              </h2>
              <div className="bg-gray-50 dark:bg-[#1A3330] p-6 rounded-xl mb-8" data-id="element-5213">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-5214">
                  Step-by-Step Pricing Process
                </h3>
                <ol className="space-y-4" data-id="element-5215">
                  <li className="flex" data-id="element-5216">
                    <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-5217">
                      1
                    </span>
                    <div data-id="element-5218">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-5219">
                        Research competitors
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5220">
                        Identify 5-10 similar products and note their pricing,
                        features, and positioning.
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-5221">
                    <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-5222">
                      2
                    </span>
                    <div data-id="element-5223">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-5224">
                        Calculate your costs
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5225">
                        Include time spent, software costs, and platform fees.
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-5226">
                    <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-5227">
                      3
                    </span>
                    <div data-id="element-5228">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-5229">
                        Assess your unique value
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5230">
                        What makes your product better or different? This can
                        justify higher pricing.
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-5231">
                    <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-5232">
                      4
                    </span>
                    <div data-id="element-5233">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-5234">
                        Consider your target audience
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5235">
                        Are they professionals who can expense purchases?
                        Hobbyists? Students?
                      </p>
                    </div>
                  </li>
                  <li className="flex" data-id="element-5236">
                    <span className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5" data-id="element-5237">
                      5
                    </span>
                    <div data-id="element-5238">
                      <p className="font-medium text-gray-800 dark:text-[#F8FAF9] mb-1" data-id="element-5239">
                        Test multiple price points
                      </p>
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5240">
                        If possible, A/B test different prices or adjust pricing
                        over time to find the optimal point.
                      </p>
                    </div>
                  </li>
                </ol>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mt-12 mb-6" data-id="element-5241">
                Pricing Models for Digital Products
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-5242">
                <div className="bg-white dark:bg-[#162825] border border-gray-200 dark:border-[#1A3330] rounded-xl p-6 shadow-sm" data-id="element-5243">
                  <div className="flex items-center mb-4" data-id="element-5244">
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3" data-id="element-5245">
                      <BarChartIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" data-id="element-5246" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-5247">
                      Tiered Pricing
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-5248">
                    Offer multiple versions of your product at different price
                    points (e.g., Basic, Pro, Enterprise).
                  </p>
                  <div className="space-y-2" data-id="element-5249">
                    <div className="flex items-start" data-id="element-5250">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-5251" />
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5252">
                        Captures customers at different budget levels
                      </p>
                    </div>
                    <div className="flex items-start" data-id="element-5253">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-5254" />
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5255">
                        Creates natural upsell opportunities
                      </p>
                    </div>
                    <div className="flex items-start" data-id="element-5256">
                      <XIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-5257" />
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5258">
                        Can be complex to manage multiple versions
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-[#162825] border border-gray-200 dark:border-[#1A3330] rounded-xl p-6 shadow-sm" data-id="element-5259">
                  <div className="flex items-center mb-4" data-id="element-5260">
                    <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mr-3" data-id="element-5261">
                      <TrendingUpIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" data-id="element-5262" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-5263">
                      Freemium
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-5264">
                    Offer a free basic version with paid upgrades for premium
                    features.
                  </p>
                  <div className="space-y-2" data-id="element-5265">
                    <div className="flex items-start" data-id="element-5266">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-5267" />
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5268">
                        Lower barrier to entry for new customers
                      </p>
                    </div>
                    <div className="flex items-start" data-id="element-5269">
                      <CheckIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-5270" />
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5271">
                        Builds trust before asking for payment
                      </p>
                    </div>
                    <div className="flex items-start" data-id="element-5272">
                      <XIcon className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-5273" />
                      <p className="text-gray-600 dark:text-[#B0C1BD]" data-id="element-5274">
                        Free users may never convert to paid
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-t border-gray-200 dark:border-[#1A3330] pt-10 mt-10" data-id="element-5275">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-5276">
                  Case Study: Successful Pricing Optimization
                </h2>
                <div className="bg-gray-50 dark:bg-[#1A3330] rounded-xl p-6 mb-8" data-id="element-5277">
                  <div className="flex items-center mb-4" data-id="element-5278">
                    <div className="w-12 h-12 rounded-full overflow-hidden mr-4" data-id="element-5279">
                      <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="Alex Morgan" className="w-full h-full object-cover" data-id="element-5280" />
                    </div>
                    <div data-id="element-5281">
                      <h3 className="font-medium text-gray-800 dark:text-[#F8FAF9]" data-id="element-5282">
                        Alex Morgan
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-5283">
                        UI Kit Creator
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-5284">
                    "I initially priced my UI kit at $39, which was lower than
                    competitors because I was new to the market. After 3 months,
                    I raised the price to $59 and added more components to
                    justify the increase."
                  </p>
                  <p className="text-gray-600 dark:text-[#B0C1BD] mb-4" data-id="element-5285">
                    "The surprising result: not only did sales volume stay
                    roughly the same, but my revenue increased by 51%. I later
                    introduced a basic version at $39 and an extended version at
                    $89, which further increased total revenue by offering
                    options for different customer segments."
                  </p>
                  <div className="mt-6 grid grid-cols-3 gap-4 text-center" data-id="element-5286">
                    <div className="bg-white dark:bg-[#162825] p-4 rounded-lg" data-id="element-5287">
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-5288">
                        Initial Price
                      </p>
                      <p className="text-xl font-bold text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-5289">
                        $39
                      </p>
                    </div>
                    <div className="bg-white dark:bg-[#162825] p-4 rounded-lg" data-id="element-5290">
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-5291">
                        Optimal Price
                      </p>
                      <p className="text-xl font-bold text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-5292">
                        $59
                      </p>
                    </div>
                    <div className="bg-white dark:bg-[#162825] p-4 rounded-lg" data-id="element-5293">
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-5294">
                        Revenue Increase
                      </p>
                      <p className="text-xl font-bold text-[#2DB489] dark:text-[#4ED1A0]" data-id="element-5295">
                        +51%
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-[#0D3C45]/5 dark:bg-[#0D3C45]/20 rounded-xl p-6 mt-12" data-id="element-5296">
                <h2 className="text-xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-5297">
                  Key Takeaways
                </h2>
                <ul className="space-y-3" data-id="element-5298">
                  <li className="flex items-start" data-id="element-5299">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-5300">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-5301" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-5302">
                      Don't undervalue your work - many creators price too low
                      initially
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-5303">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-5304">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-5305" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-5306">
                      Test different price points - the market will tell you
                      what works
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-5307">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-5308">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-5309" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-5310">
                      Consider offering multiple tiers to capture different
                      customer segments
                    </p>
                  </li>
                  <li className="flex items-start" data-id="element-5311">
                    <div className="bg-[#2DB489] dark:bg-[#4ED1A0] rounded-full p-1 mr-3 mt-1 flex-shrink-0" data-id="element-5312">
                      <CheckIcon className="h-3 w-3 text-white" data-id="element-5313" />
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD]" data-id="element-5314">
                      Focus on communicating value, not just features or
                      specifications
                    </p>
                  </li>
                </ul>
              </div>
            </div>
            {/* Related resources */}
            <div className="bg-white dark:bg-[#162825] rounded-xl p-8 shadow-sm mb-10" data-id="element-5315">
              <h2 className="text-xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-6" data-id="element-5316">
                Related Resources
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5317">
                <a href="/learn/product-bundles" className="group block bg-gray-50 dark:bg-[#1A3330] rounded-lg p-5 transition-all hover:shadow-md" data-id="element-5318">
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2 group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-5319">
                    Product Bundles That Sell
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-5320">
                    Learn how to create irresistible product bundles that
                    increase your average order value.
                  </p>
                  <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium" data-id="element-5321">
                    <span data-id="element-5322">Read more</span>
                    <ArrowRightIcon className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-5323" />
                  </div>
                </a>
                <a href="/learn/limited-offers" className="group block bg-gray-50 dark:bg-[#1A3330] rounded-lg p-5 transition-all hover:shadow-md" data-id="element-5324">
                  <h3 className="font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2 group-hover:text-[#2DB489] dark:group-hover:text-[#4ED1A0] transition-colors" data-id="element-5325">
                    Creating Limited-Time Offers
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-[#B0C1BD] mb-3" data-id="element-5326">
                    Strategies for creating urgency and scarcity to boost sales
                    of your digital products.
                  </p>
                  <div className="flex items-center text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium" data-id="element-5327">
                    <span data-id="element-5328">Read more</span>
                    <ArrowRightIcon className="ml-1 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-5329" />
                  </div>
                </a>
              </div>
            </div>
            {/* CTA */}
            <div className="bg-gradient-to-r from-[#0D3C45] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] rounded-xl p-8 text-white text-center" data-id="element-5330">
              <h2 className="text-2xl font-bold mb-4" data-id="element-5331">
                Ready to optimize your pricing?
              </h2>
              <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto" data-id="element-5332">
                Apply these strategies to your digital products and start
                maximizing your revenue today.
              </p>
              <a href="/sell-product" className="inline-block bg-white text-[#0D3C45] px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-all" data-id="element-5333">
                Start Selling
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-5334" />
    </div>;
};